import React from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox, MediaPlayer } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import { RESPONSE_STATES } from 'utils/constants'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage, {
  hasQuestionTitleImage,
} from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getChoiceAnswerValue,
  getShouldRenderNextQuestion,
  resolveChoiceIdFromStoredAnswer,
} from '../../utils/instrumentUtils'

const validateSelectMultiple = (attributes = {}, selectedCount) => {
  const {
    required = false,
    min_choices: minChoices = null,
    max_choices: maxChoices = null,
  } = attributes || {}

  const showSkip = !required

  const parseLimit = (value) => {
    if (value === null || value === undefined || value === '') return null
    const num = Number(value)
    return Number.isNaN(num) ? null : num
  }

  const min = parseLimit(minChoices)
  const max = parseLimit(maxChoices)

  // 2) ZERO SELECTION RULE
  if (selectedCount === 0) {
    if (required) {
      return {
        isValid: false,
        showSkip,
      }
    }

    if (min !== null && min > 0) {
      return {
        isValid: false,
        showSkip,
      }
    }

    return {
      isValid: true,
      showSkip,
    }
  }

  // 3) NON-ZERO SELECTION RULE
  if (min !== null && max !== null) {
    if (selectedCount >= min && selectedCount <= max) {
      return {
        isValid: true,
        showSkip,
      }
    }

    return {
      isValid: false,
      showSkip,
    }
  }

  if (min !== null) {
    if (selectedCount >= min) {
      return {
        isValid: true,
        showSkip,
      }
    }

    return {
      isValid: false,
      showSkip,
    }
  }

  if (max !== null) {
    if (selectedCount <= max) {
      return {
        isValid: true,
        showSkip,
      }
    }

    return {
      isValid: false,
      showSkip,
    }
  }

  // 4) FALLBACK
  if (!required && min === null && max === null) {
    return {
      isValid: true,
      showSkip,
    }
  }

  return {
    isValid: true,
    showSkip,
  }
}

class SelectMultiple extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selected: [],
      selectingChoice: null,
      deselectingChoice: null,
      completed: false,
      hasInteracted: false,
    }
    this.firstButtonRef = React.createRef()
  }

  componentDidMount() {
    const { storedAnswer, suppressAutoAdvance } = this.props

    if (storedAnswer?.length) {
      const { choices } = this.props.question || {}
      // Restore all stored answers synchronously
      storedAnswer.forEach((answer) => {
        const choiceId = resolveChoiceIdFromStoredAnswer(choices, answer)
        if (choiceId) this.onSelectChoice(choiceId)
      })
      // Mark as interacted and complete if needed
      this.setState({ hasInteracted: true }, () => {
        if (!suppressAutoAdvance) {
          this.onCompleteSelectMultipleQuestion()
        }
      })
    }
  }

  componentWillUnmount() {
    // No cleanup needed - using event-driven approach
  }

  onSetCompletion = () => {
    const { selected } = this.state
    if (selected.length > 0) {
      this.setState({ completed: true })
    } else {
      this.setState({ completed: false })
    }
  }

  handleAnimationEnd = (e) => {
    // Only handle our specific animation to avoid interfering with child animations
    if (e.animationName === 'selecting') {
      const { selectingChoice, deselectingChoice } = this.state
      if (selectingChoice) {
        this.setState({ selectingChoice: null })
      }
      if (deselectingChoice) {
        this.setState({ deselectingChoice: null })
      }
    }
  }

  onSelectChoice = (id) => {
    const { id: questionId, setAllRequiredAnswered } = this.props
    const { selected } = this.state
    let _selected = selected.slice(0)
    if (selected.includes(id)) {
      _selected = _selected.filter((el) => el !== id)
      this.setState(
        { selected: _selected, deselectingChoice: id, hasInteracted: true },
        () => {
          this.onSetCompletion()
          // Autosave latest multi-select into Redux so sibling pages can enable Next
          // without requiring per-question footers.
          try {
            const { question, webSurvey, completeQuestion } = this.props
            const { label, type, attributes } = question
            const { required } = attributes
            const answer = this.buildChoiceAnswers(
              question.choices,
              _selected,
              questionId,
            )
            const submission = {
              id: questionId,
              label,
              name: label,
              type,
              required,
              state: _selected.length === 0 ? 'SKIPPED' : 'ANSWERED',
              answer,
            }
            if (typeof completeQuestion === 'function' && webSurvey) {
              completeQuestion(submission, webSurvey.order?.[0])
            }
          } catch (e) {
            console.warn('SelectMultiple autosave failed', { error: e })
          }
        },
      )
      // Animation cleanup handled by onAnimationEnd event
    } else {
      _selected.push(id)
      this.setState(
        { selected: _selected, selectingChoice: id, hasInteracted: true },
        () => {
          this.onSetCompletion()
          // Autosave latest multi-select into Redux so sibling pages can enable Next
          // without requiring per-question footers.
          try {
            const { question, webSurvey, completeQuestion } = this.props
            const { label, type, attributes } = question
            const { required } = attributes
            const answer = this.buildChoiceAnswers(
              question.choices,
              _selected,
              questionId,
            )
            const submission = {
              id: questionId,
              label,
              name: label,
              type,
              required,
              state: _selected.length === 0 ? 'SKIPPED' : 'ANSWERED',
              answer,
            }
            if (typeof completeQuestion === 'function' && webSurvey) {
              completeQuestion(submission, webSurvey.order?.[0])
            }
          } catch (e) {
            console.warn('SelectMultiple autosave failed', { error: e })
          }
        },
      )
      // Animation cleanup handled by onAnimationEnd event
    }
    setAllRequiredAnswered(false, questionId)

    // Autosave latest multi-select into submission without advancing
    try {
      const { question, webSurvey, completeQuestion } = this.props
      const { choices, label, type, attributes } = question
      const { required } = attributes
      const selectedNow = _selected
      const answer = selectedNow.map((choiceId) => ({
        label: choices[choiceId].label,
        value: choiceId,
        question: questionId,
      }))
      const submission = {
        id: questionId,
        label,
        name: label,
        type,
        required,
        state:
          selectedNow.length === 0
            ? RESPONSE_STATES.skipped
            : RESPONSE_STATES.answered,
        answer,
      }
      if (typeof completeQuestion === 'function' && webSurvey) {
        completeQuestion(submission, webSurvey.order?.[0])
      }
    } catch (e) {
      console.warn('SelectMultiple autosave failed', { error: e })
    }
  }

  buildChoiceAnswers = (choices, choiceIds, questionId) =>
    choiceIds.map((choiceId) => ({
      label: choices?.[choiceId]?.label,
      value: getChoiceAnswerValue(choices, choiceId),
      question: questionId,
      choice_id: choiceId,
    }))

  createAnswerObjectsArr = () => {
    const { question, id } = this.props
    const { choices } = question
    const { selected } = this.state
    return this.buildChoiceAnswers(choices, selected, id).map((item) => ({
      ...item,
      unit: choices?.[item.choice_id]?.label,
    }))
  }

  onCompleteSelectMultipleQuestion = () => {
    const {
      question,
      id,
      completeQuestion,
      nextQ,
      webSurvey,
      submissionSurvey,
      nextQId,
      reviewFunctionalitySupported,
      isConsent = false,
      isLastQuestion = false,
      isSibling = false,
    } = this.props
    const { selected } = this.state
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = this.createAnswerObjectsArr()
    const submission = {
      id,
      label,
      name: label,
      type,
      required,
      state:
        selected.length === 0
          ? RESPONSE_STATES.skipped
          : RESPONSE_STATES.answered,
      answer,
    }
    const currentAnswer = { [id]: answer }

    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })

    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ,
    )
  }

  renderChoices = (choicesOrder, hasQuestionImage) => {
    const {
      question,
      id,
      renderBackButton,
      strings,
      submissionSurvey,
      webSurvey,
      isLastQuestion,
      isConsent,
      submissionLoading,
      isSibling = false,
    } = this.props
    const { choices, attributes, mediaSrc } = question
    const {
      required,
      disable_buttons: disableButtons,
      min_choices,
      max_choices,
    } = attributes
    const { selected, selectingChoice, deselectingChoice, completed } =
      this.state
    const selectedCount = selected.length
    const { isValid } = validateSelectMultiple(
      { required, min_choices, max_choices },
      selectedCount,
    )
    const hasSelection = selectedCount > 0

    let hasImages
    return (
      <div className={`question-choices${hasQuestionImage ? ' right' : ''}`}>
        <MakeScrollable>
          {choicesOrder.map((choiceId) => {
            const choice = choices[choiceId]
            const { imageSrc } = choice
            if (imageSrc) hasImages = true
            return (
              <Button
                onClick={() => this.onSelectChoice(choiceId, required)}
                onAnimationEnd={this.handleAnimationEnd}
                key={`${choiceId}`}
                className={`choice${selectingChoice === choiceId ? ' selecting' : ''}${
                  deselectingChoice === choiceId ? ' deselecting' : ''
                }${selected.includes(choiceId) ? ' selected' : ''}${hasImages ? ' column' : ''}${
                  disableButtons ? ' no-icon' : ''
                }`}>
                {!hasImages && !disableButtons && (
                  <Checkbox
                    check
                    checked={choiceId === selected}
                    transition={
                      choiceId === selectingChoice ||
                      choiceId === deselectingChoice
                    }
                  />
                )}
                {imageSrc && <img src={imageSrc} alt={`choice-${choiceId}`} />}
                <div className={`text ${hasImages ? '' : 'no-img'}`}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(choice.label),
                    }}
                  />
                </div>
                {hasImages && !disableButtons && (
                  <Checkbox
                    check
                    checked={choiceId === selected}
                    transition={
                      choiceId === selectingChoice ||
                      choiceId === deselectingChoice
                    }
                  />
                )}
              </Button>
            )
          })}
          {!isSibling && (
            <QuestionFooter
              completed={completed}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={() => {
                this.onCompleteSelectMultipleQuestion()
              }}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </MakeScrollable>
      </div>
    )
  }

  render() {
    const { question, id, className } = this.props
    const { choices_order, label, imageSrc, attributes } = question
    const _className = className ? ` ${className}` : ''
    const hasQImage = hasQuestionTitleImage(imageSrc)

    return (
      <div ref={this.props.forwardedRef}>
        <SurveyQuestion id={id}>
          <MakeScrollable>
            <div
              className={`survey-question select multiple${_className}${hasQImage ? ' has-image' : ''}`}>
              <p className={`question-label${hasQImage ? ' left' : ''}`}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatAndSanitizeHTML(label),
                  }}
                />
                <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
              </p>
              {this.renderChoices(choices_order, hasQImage)}
            </div>
          </MakeScrollable>
        </SurveyQuestion>
      </div>
    )
  }
}

const ForwardedSelectMultiple = React.forwardRef((props, ref) => (
  <SelectMultiple {...props} forwardedRef={ref} />
))

export default ForwardedSelectMultiple

SelectMultiple.propTypes = {
  className: PropTypes.string,
  completeQuestion: PropTypes.func,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  nextQ: PropTypes.func,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}
